import {all, takeLatest} from '@redux-saga/core/effects';
import {AsyncResult, fromResult, Loading, LoadingAdditional} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {CorrespondenceLetterSummary} from 'Agent/Policies/api/apiTypes';
import policyApi from 'Agent/Policies/api/policyApi';
import {
  changeCorrespondenceDetails,
  changeCorrespondenceSummaries,
  getCorrespondenceDetails,
  getCorrespondenceSummaries,
  PolicyCorrespondenceActionType,
} from 'Agent/Policies/ducks/policyCorrespondence';
import {CorrespondenceDetail} from 'Agent/Policies/models/CorrespondenceDetail';
import {call, put} from 'redux-saga/effects';
import {get} from 'Shared/utilities/fetchHelpers';
import {mergeWithNextPage, removeApiPrefix} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';

export function* getSummaries(action: ReturnType<typeof getCorrespondenceSummaries>) {
  const {sort, page, isNewYork, encryptedIdentifier, filter, agentNumbers} = action.payload;
  if (page) {
    yield put(changeCorrespondenceSummaries(new LoadingAdditional(page)));
  } else {
    yield put(changeCorrespondenceSummaries(new Loading(page)));
  }

  const result: AsyncResult<Page<CorrespondenceLetterSummary>> = yield page
    ? call(mergeWithNextPage, {existingPage: page})
    : call(
        policyApi.getCorrespondenceSummaries,
        agentNumbers.map(prop('value')),
        isNewYork,
        encryptedIdentifier,
        filter,
        sort
      );

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeCorrespondenceSummaries(value)),
      value => put(changeCorrespondenceSummaries(value))
    )
  );
}

export function getDetails(action: ReturnType<typeof getCorrespondenceDetails>) {
  return get<CorrespondenceDetail[]>(removeApiPrefix(action.payload.link));
}

export function* getCorrespondenceSummariesSaga() {
  yield takeLatest(PolicyCorrespondenceActionType.GET_POLICY_CORRESPONDENCE_SUMMARIES, getSummaries);
}

export function* getCorrespondenceDetailsSaga() {
  yield takeLatest(
    PolicyCorrespondenceActionType.GET_POLICY_CORRESPONDENCE_DETAILS,
    getApiCallGenerator(getDetails, changeCorrespondenceDetails)
  );
}

export default function*() {
  yield all([getCorrespondenceSummariesSaga(), getCorrespondenceDetailsSaga()]);
}
